import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// Interfaces
export interface ProjectStage {
  id: number;
  name: string;
  description: string;
  order_sequence: number;
  is_active: boolean;
  requires_approval: boolean;
  approval_level: number;
  created_at: string;
  updated_at: string;
}

export interface StageTransition {
  id: number;
  from_stage_id: number;
  to_stage_id: number;
  is_allowed: boolean;
  requires_approval: boolean;
  approval_level: number;
  to_stage_name: string;
  to_stage_description: string;
  to_stage_order: number;
  created_at: string;
}

export interface ApprovalRequest {
  id: number;
  project_id: number;
  from_stage_id: number;
  to_stage_id: number;
  requester_id: number;
  approver_id: number;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  request_reason: string;
  approval_notes: string;
  requested_at: string;
  approved_at: string;
  created_at: string;
  updated_at: string;
  // Additional fields from view
  project_name?: string;
  project_description?: string;
  from_stage_name?: string;
  to_stage_name?: string;
  requester_name?: string;
  approver_name?: string;
  history?: WorkflowHistory[];
}

export interface WorkflowHistory {
  id: number;
  approval_request_id: number;
  action: string;
  actor_id: number;
  actor_name: string;
  actor_role: string;
  notes: string;
  created_at: string;
}

export interface WorkflowStats {
  pending_approvals: number;
  my_requests: number;
  approved_requests: number;
  rejected_requests: number;
}

export interface CreateApprovalRequest {
  projectId: number;
  toStageId: number;
  requestReason: string;
}

export interface ProcessApproval {
  action: 'approve' | 'reject';
  notes: string;
}

export interface PaginationResponse<T> {
  status: string;
  data: T[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_items: number;
    items_per_page: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ApprovalWorkflowService {
  private apiUrl = `${environment.BASE_URL}/approval-workflow`;

  constructor(private http: HttpClient) {}

  // Project Stages
  getProjectStages(): Observable<ProjectStage[]> {
    return this.http.get<ProjectStage[]>(`${this.apiUrl}/stages`);
  }

  getStageTransitions(stageId: number): Observable<StageTransition[]> {
    return this.http.get<StageTransition[]>(`${this.apiUrl}/stages/${stageId}/transitions`);
  }

  // Approval Requests
  createApprovalRequest(request: CreateApprovalRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/requests`, request);
  }

  getApprovalRequests(params?: {
    status?: string;
    page?: number;
    limit?: number;
  }): Observable<PaginationResponse<ApprovalRequest>> {
    let httpParams = new HttpParams();
    if (params) {
    //   Object.keys(params).forEach((key:any) => {
    //     if (params[key] !== null && params[key] !== undefined) {
    //       httpParams = httpParams.set(key, params[key].toString());
    //     }
    //   });
    }
    return this.http.get<PaginationResponse<ApprovalRequest>>(`${this.apiUrl}/requests`, { params: httpParams });
  }

  getApprovalRequest(id: number): Observable<ApprovalRequest> {
    return this.http.get<ApprovalRequest>(`${this.apiUrl}/requests/${id}`);
  }

  processApproval(id: number, data: ProcessApproval): Observable<any> {
    return this.http.put(`${this.apiUrl}/requests/${id}/process`, data);
  }

  cancelApprovalRequest(id: number): Observable<any> {
    return this.http.put(`${this.apiUrl}/requests/${id}/cancel`, {});
  }

  // Workflow Statistics
  getWorkflowStats(): Observable<WorkflowStats> {
    return this.http.get<WorkflowStats>(`${this.apiUrl}/stats`);
  }

  // Utility methods
  getStatusClass(status: string): string {
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'approved':
        return 'status-approved';
      case 'rejected':
        return 'status-rejected';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return 'status-default';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'pending':
        return 'schedule';
      case 'approved':
        return 'check_circle';
      case 'rejected':
        return 'cancel';
      case 'cancelled':
        return 'block';
      default:
        return 'help';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'pending':
        return 'Pending Approval';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  }

  canApprove(request: ApprovalRequest, currentUserId: number): boolean {
    return request.status === 'pending' && request.approver_id === currentUserId;
  }

  canCancel(request: ApprovalRequest, currentUserId: number): boolean {
    return request.status === 'pending' && request.requester_id === currentUserId;
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
